import React, { useState } from 'react';
import { AlertTriangle, RotateCcw, Home, Copy, Check, ShieldCheck, LifeBuoy } from 'lucide-react';

export interface ErrorFallbackProps {
  error?: Error | { message?: string; stack?: string } | null;
  resetErrorBoundary?: () => void;
  is404?: boolean;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  is404 = false
}) => {
  const [copied, setCopied] = useState(false);

  const rawMessage = error?.message || (is404 ? 'Page ou ressource non trouvée (404)' : "L'application a rencontré un problème temporaire.");
  const isActually404 = is404 || /404|not found|introuvable/i.test(rawMessage);

  const handleCopy = () => {
    const textToCopy = `Erreur Zeyilo:\n${rawMessage}\n\nURL: ${window.location.href}\nDate: ${new Date().toISOString()}`;
    navigator.clipboard.writeText(textToCopy).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  return (
    <div className="min-h-[100dvh] w-full flex flex-col items-center justify-center bg-white px-4 sm:px-6 py-8 select-none">
      <div className="w-full max-w-md space-y-6">
        {/* Logo Zeyilo */}
        <div className="text-center flex flex-col items-center">
          <a href="/dashboard" className="inline-block transition-transform hover:scale-[1.02]">
            <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1" />
          </a>
          <p className="text-slate-500 text-xs sm:text-sm font-medium">Agent IA WhatsApp</p>
        </div>

        {/* Conteneur Bento principal */}
        <div className="bg-slate-100/80 rounded-2xl sm:rounded-3xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
          {/* Header Bento */}
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 border-0 shadow-none ${
              isActually404 ? 'bg-white text-indigo-600' : 'bg-white text-amber-600'
            }`}>
              <AlertTriangle className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                  {isActually404 ? 'Page non trouvée (404)' : 'Une erreur inattendue est survenue'}
                </h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  isActually404 ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-800'
                }`}>
                  {isActually404 ? '404' : 'Incident temporaire'}
                </span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-snug">
                {isActually404
                  ? "La page recherchée n'existe pas ou a été déplacée."
                  : "L'affichage a été interrompu. Vos données et votre agent IA restent en sécurité."}
              </p>
            </div>
          </div>

          {/* Sous-Bento blanc : Détail de l'erreur */}
          {!isActually404 && (
            <div className="bg-white rounded-xl p-3 sm:p-3.5 border-0 shadow-none space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
                  Détail de l'incident
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 cursor-pointer transition-colors"
                  title="Copier le détail technique"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-600 font-bold">Copié</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copier</span>
                    </>
                  )}
                </button>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 select-text">
                <p className="text-xs text-rose-700 font-mono break-words leading-relaxed">
                  {rawMessage}
                </p>
              </div>
            </div>
          )}

          {/* Sous-Bento blanc : Actions rapides */}
          <div className="bg-white rounded-xl p-3 sm:p-3.5 border-0 shadow-none space-y-2">
            <button
              type="button"
              onClick={handleReload}
              className="w-full py-2.5 sm:py-3 px-4 rounded-xl bg-slate-900 hover:bg-black text-white font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer shadow-none active:scale-[0.98]"
            >
              <RotateCcw className="w-4 h-4 stroke-[2.2]" />
              <span>Recharger la page</span>
            </button>

            <div className="grid grid-cols-2 gap-2 pt-0.5">
              <button
                type="button"
                onClick={handleGoHome}
                className="w-full py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200/80 text-slate-800 font-medium text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              >
                <Home className="w-3.5 h-3.5 text-slate-600" />
                <span>Tableau de bord</span>
              </button>

              <a
                href="mailto:support@zeyilo.com"
                className="w-full py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200/80 text-slate-800 font-medium text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer text-center"
              >
                <LifeBuoy className="w-3.5 h-3.5 text-slate-600" />
                <span>Assistance</span>
              </a>
            </div>
          </div>
        </div>

        {/* Note de réassurance discrète */}
        <div className="flex items-center justify-center gap-2 text-center text-[11px] text-slate-400">
          <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Services de réponse IA et WhatsApp opérationnels</span>
        </div>
      </div>
    </div>
  );
};
